import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { parseEvolutionError } from '@/lib/evolutionError';
import { getStatusConfig } from '@/mocks/campaignsData';

interface CampaignData {
  id: string;
  name: string;
  status: string;
  message_text: string;
  total_contacts: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  replied_count: number;
  failed_count: number;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  target_type: string;
  target_groups: string[];
  media_type: string | null;
  media_url: string | null;
}

interface QueueJobRow {
  id: string;
  campaign_id: string;
  contact_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  attempt: number;
  max_attempts: number;
  last_error: string | null;
  created_at: string;
  processed_at: string | null;
  contacts?: {
    name: string;
    phone: string;
  } | null;
}

interface QueueJobDisplay {
  id: string;
  contactName: string;
  contactPhone: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  attempt: number;
  maxAttempts: number;
  lastError: string | null;
  createdAt: string;
  processedAt: string | null;
}

const statusBadge: Record<QueueJobDisplay['status'], { bg: string; text: string; icon: string }> = {
  queued: { bg: 'bg-background-100', text: 'text-foreground-500', icon: 'ri-time-line' },
  processing: { bg: 'bg-accent-50', text: 'text-accent-700', icon: 'ri-loader-4-line animate-spin' },
  completed: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'ri-check-line' },
  failed: { bg: 'bg-red-50', text: 'text-red-700', icon: 'ri-close-line' },
};

const statusLabel: Record<QueueJobDisplay['status'], string> = {
  queued: 'Na fila',
  processing: 'Enviando',
  completed: 'Enviado',
  failed: 'Falhou',
};

export default function CampaignProgressPage() {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<CampaignData | null>(null);
  const [loadingCampaign, setLoadingCampaign] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [jobs, setJobs] = useState<QueueJobDisplay[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [populating, setPopulating] = useState(false);
  const [populateResult, setPopulateResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [processingQueue, setProcessingQueue] = useState(false);
  const [queueResult, setQueueResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [processingBrowser, setProcessingBrowser] = useState(false);
  const [browserProgress, setBrowserProgress] = useState({ current: 0, total: 0 });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Number verification state
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{ existing: number; notExisting: number; details: Array<{ phone: string; contactName: string; exists: boolean }> } | null>(null);
  const [removingInvalid, setRemovingInvalid] = useState(false);

  // Fetch campaign from Supabase
  const fetchCampaign = useCallback(async () => {
    if (!id) return;
    setLoadingCampaign(true);
    setFetchError(null);

    const { data, error } = await supabase
      .from('campaigns')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      setFetchError(error.message);
      setLoadingCampaign(false);
      return;
    }

    if (!data) {
      setFetchError('Campanha não encontrada');
      setLoadingCampaign(false);
      return;
    }

    setCampaign(data as CampaignData);

    const pct = data.total_contacts > 0
      ? Math.round((data.sent_count / data.total_contacts) * 100)
      : 0;
    setProgress(pct);
    setIsRunning(data.status === 'ativa' && pct < 100);
    setLoadingCampaign(false);
  }, [id]);

  const fetchJobs = useCallback(async () => {
    if (!id) return;

    const { data, error } = await supabase
      .from('queue_jobs')
      .select('id, campaign_id, contact_id, status, attempt, max_attempts, last_error, created_at, processed_at, contacts!inner(name, phone)')
      .eq('campaign_id', id)
      .order('created_at', { ascending: false })
      .limit(100);

    setLoadingJobs(false);

    if (error) {
      return;
    }

    const rows = data as unknown as QueueJobRow[];
    const displayJobs: QueueJobDisplay[] = rows.map((j) => ({
      id: j.id,
      contactName: j.contacts?.name || '—',
      contactPhone: j.contacts?.phone || '—',
      status: j.status,
      attempt: j.attempt,
      maxAttempts: j.max_attempts,
      lastError: j.last_error,
      createdAt: j.created_at,
      processedAt: j.processed_at,
    }));

    setJobs(displayJobs);
  }, [id]);

  // Fetch on mount and poll
  useEffect(() => {
    fetchCampaign();
    fetchJobs();

    intervalRef.current = setInterval(() => {
      if (autoRefresh) {
        fetchCampaign();
        fetchJobs();
      }
    }, 3000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchCampaign, fetchJobs, autoRefresh]);

  // Populate queue for this campaign
  const handlePopulateQueue = async () => {
    if (!id) return;
    setPopulating(true);
    setPopulateResult(null);

    const { data, error } = await supabase.functions.invoke('populate-campaign-queue', {
      body: { campaign_id: id },
    });

    setPopulating(false);

    if (error || !data?.success) {
      setPopulateResult({
        type: 'error',
        text: data?.message || error?.message || 'Erro ao popular fila',
      });
    } else {
      setPopulateResult({
        type: 'success',
        text: data.message || `${data.created || 0} jobs criados na fila!`,
      });
      fetchCampaign();
      fetchJobs();
    }
    setTimeout(() => setPopulateResult(null), 6000);
  };

  const handleProcessQueue = async () => {
    if (processingQueue || processingBrowser) return;
    setProcessingQueue(true);
    setQueueResult(null);

    let totalProcessed = 0;
    let totalFailed = 0;
    let keepGoing = true;
    const maxIterations = 60;
    let iteration = 0;

    while (keepGoing && iteration < maxIterations) {
      iteration++;

      const { data, error } = await supabase.functions.invoke('process-queue', {
        body: {},
      });

      if (error) {
        setProcessingQueue(false);
        setQueueResult({ type: 'error', text: 'Edge Function indisponível. Tentando envio direto pelo navegador...' });
        setTimeout(() => processQueueFromBrowser(), 500);
        return;
      }

      if (!data?.success) {
        setProcessingQueue(false);
        setQueueResult({ type: 'error', text: data?.message || 'Erro ao processar fila' });
        fetchJobs();
        fetchCampaign();
        setTimeout(() => setQueueResult(null), 6000);
        return;
      }

      totalProcessed += data.processed || 0;
      totalFailed += data.failed || 0;
      const remaining = data.remaining || 0;

      setQueueResult({
        type: 'success',
        text: `Processando... ${totalProcessed} enviadas, ${totalFailed} falhas${remaining > 0 ? ` (${remaining} restantes)` : ''}`,
      });
      fetchJobs();
      fetchCampaign();

      if (remaining === 0) {
        keepGoing = false;
      } else {
        await new Promise((r) => setTimeout(r, 1500));
      }
    }

    setProcessingQueue(false);
    setQueueResult({
      type: 'success',
      text: `Fila processada! ${totalProcessed} enviadas, ${totalFailed} falhas.`,
    });
    fetchJobs();
    fetchCampaign();
    setTimeout(() => setQueueResult(null), 8000);
  };

  // Browser-side queue processor
  const processQueueFromBrowser = async () => {
    if (processingBrowser) return;
    setProcessingBrowser(true);

    try {
      const { data: waConfig } = await supabase
        .from('whatsapp_configs')
        .select('*')
        .maybeSingle();

      if (!waConfig?.evolution_server_url || !waConfig?.evolution_api_key || !waConfig?.evolution_instance_name) {
        setQueueResult({ type: 'error', text: 'Evolution API não configurada.' });
        setProcessingBrowser(false);
        return;
      }

      const baseUrl = waConfig.evolution_server_url.replace(/\/+$/, '');
      const instanceName = waConfig.evolution_instance_name;
      const apiKey = waConfig.evolution_api_key;
      const rateLimitPerMinute = waConfig.rate_limit_per_minute || 30;
      const delayMs = Math.max(1000, Math.floor(60000 / rateLimitPerMinute));

      // Clean tracking params from URLs (YouTube ?si=, etc) and convert short URLs
      const cleanUrl = (url: string): string => {
        try {
          const u = new URL(url);
          // Convert youtu.be short URLs to full youtube.com/watch?v= URLs
          if (u.hostname === 'youtu.be') {
            const videoId = u.pathname.replace('/', '');
            if (videoId) {
              u.hostname = 'www.youtube.com';
              u.pathname = '/watch';
              u.search = `?v=${videoId}`;
            }
          }
          // Remove tracking parameters from all YouTube URLs
          if (u.hostname.includes('youtube.com') || u.hostname.includes('youtu.be')) {
            u.searchParams.delete('si');
            u.searchParams.delete('feature');
            u.searchParams.delete('pp');
            u.searchParams.delete('utm_source');
            u.searchParams.delete('utm_medium');
            u.searchParams.delete('utm_campaign');
          }
          return u.toString();
        } catch {
          return url;
        }
      };

      // Build message helper - also cleans URLs inside the message text
      const cleanMessageUrls = (msg: string): string => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return msg.replace(urlRegex, (url) => cleanUrl(url));
      };
      const buildMsg = (text: string, link: string | null) => {
        const cleanedText = cleanMessageUrls(text);
        if (!link) return cleanedText;
        // Append link after text so WhatsApp treats it like a naturally pasted URL
        return `${cleanedText}\n\n${cleanUrl(link)}`;
      };

      // Check instance state
      try {
        const stateUrl = `${baseUrl}/instance/connectionState/${instanceName}`;
        const stateRes = await fetch(stateUrl, { headers: { apikey: apiKey } });
        if (stateRes.ok) {
          const stateData = await stateRes.json();
          if (stateData?.instance?.state !== 'open') {
            setQueueResult({
              type: 'error',
              text: `Instância "${instanceName}" está "${stateData?.instance?.state || 'desconhecida'}". Reconecte o WhatsApp.`,
            });
            setProcessingBrowser(false);
            return;
          }
        }
      } catch {
        setQueueResult({ type: 'error', text: 'Servidor Evolution API offline.' });
        setProcessingBrowser(false);
        return;
      }

      // Get queued jobs
      const { data: jobsData, error: jobsError } = await supabase
        .from('queue_jobs')
        .select('*')
        .eq('status', 'queued')
        .order('created_at', { ascending: true })
        .limit(50);

      if (jobsError || !jobsData || jobsData.length === 0) {
        setQueueResult({ type: 'error', text: 'Nenhum job pendente na fila.' });
        setProcessingBrowser(false);
        return;
      }

      setBrowserProgress({ current: 0, total: jobsData.length });

      let sent = 0;
      let failed = 0;

      for (let i = 0; i < jobsData.length; i++) {
        const job = jobsData[i];
        setBrowserProgress({ current: i + 1, total: jobsData.length });

        const { data: jobCampaign } = await supabase
          .from('campaigns')
          .select('*')
          .eq('id', job.campaign_id)
          .maybeSingle();

        const { data: contact } = await supabase
          .from('contacts')
          .select('*')
          .eq('id', job.contact_id)
          .maybeSingle();

        const rawPhone = contact?.phone || '';
        const normalizePhone = (p: string): string => {
          const digits = p.replace(/\D/g, '');
          if (digits.startsWith('55')) return digits;
          return '55' + digits;
        };
        const phone = normalizePhone(rawPhone);
        const message = buildMsg(jobCampaign?.message_text || '', jobCampaign?.link_url || null);

        if (!rawPhone || !message) {
          await supabase.from('queue_jobs').update({
            status: 'failed',
            last_error: 'Dados inválidos',
            processed_at: new Date().toISOString(),
          }).eq('id', job.id);
          failed++;
          continue;
        }

        const hasMedia = jobCampaign?.media_type && jobCampaign?.media_url &&
          ['image', 'video', 'audio', 'document'].includes(jobCampaign.media_type);

        let sendUrl: string;
        let payload: Record<string, unknown>;

        if (hasMedia) {
          sendUrl = `${baseUrl}/message/sendMedia/${instanceName}`;
          payload = {
            number: phone,
            mediatype: jobCampaign.media_type,
            media: jobCampaign.media_url,
            caption: message,
          };
        } else {
          sendUrl = `${baseUrl}/message/sendText/${instanceName}`;
          payload = { number: phone, text: message, linkPreview: true, delay: 1500 };
        }

        try {
          const evoRes = await fetch(sendUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', apikey: apiKey },
            body: JSON.stringify(payload),
          });

          const evoData = await evoRes.json();

          if (!evoRes.ok) {
            const errorMsg = parseEvolutionError(evoData, phone);
            const newAttempt = (job.attempt || 0) + 1;
            const isFinal = newAttempt >= (job.max_attempts || 3);
            const isNumberNotOnWhatsApp = errorMsg.includes('nao tem WhatsApp');
            const finalStatus = isNumberNotOnWhatsApp ? 'failed' : (isFinal ? 'failed' : 'queued');

            await supabase.from('queue_jobs').update({
              attempt: newAttempt,
              last_error: errorMsg,
              status: finalStatus,
              processed_at: new Date().toISOString(),
            }).eq('id', job.id);

            if (job.campaign_id) {
              await supabase.rpc('increment_campaign_failed', { campaign_id: job.campaign_id });
            }
            failed++;
          } else {
            const msgId = evoData?.key?.id || evoData?.messageId || null;

            await supabase.from('queue_jobs').update({
              status: 'completed',
              processed_at: new Date().toISOString(),
            }).eq('id', job.id);

            const { data: existingCC } = await supabase
              .from('campaign_contacts')
              .select('id')
              .eq('campaign_id', job.campaign_id)
              .eq('contact_id', job.contact_id)
              .maybeSingle();

            if (existingCC) {
              await supabase.from('campaign_contacts').update({
                status: 'sent',
                wa_message_id: msgId,
                sent_at: new Date().toISOString(),
              }).eq('id', existingCC.id);
            } else {
              await supabase.from('campaign_contacts').insert({
                campaign_id: job.campaign_id,
                contact_id: job.contact_id,
                status: 'sent',
                wa_message_id: msgId,
                sent_at: new Date().toISOString(),
              });
            }

            if (job.campaign_id) {
              await supabase.rpc('increment_campaign_sent', { campaign_id: job.campaign_id });
            }
            sent++;
          }
        } catch {
          const newAttempt = (job.attempt || 0) + 1;
          const isFinal = newAttempt >= (job.max_attempts || 3);
          await supabase.from('queue_jobs').update({
            attempt: newAttempt,
            last_error: 'Erro de rede',
            status: isFinal ? 'failed' : 'queued',
            processed_at: new Date().toISOString(),
          }).eq('id', job.id);
          failed++;
        }

        if (i < jobsData.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }

      // Check which campaigns had jobs processed and finalize if done
      const uniqueCampaignIds = [...new Set(jobsData.map((j: { campaign_id: string }) => j.campaign_id))];
      for (const cid of uniqueCampaignIds) {
        const { data: pending } = await supabase
          .from('queue_jobs')
          .select('id')
          .eq('campaign_id', cid)
          .in('status', ['queued', 'processing'])
          .limit(1);
        if (pending && pending.length === 0) {
          await supabase
            .from('campaigns')
            .update({ status: 'finalizada', completed_at: new Date().toISOString(), updated_at: new Date().toISOString() })
            .eq('id', cid)
            .eq('status', 'ativa');
        }
      }

      // Check if more jobs remain and auto-continue
      const { count: remaining } = await supabase
        .from('queue_jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'queued');

      const remainingCount = remaining || 0;

      setQueueResult({
        type: sent > 0 ? 'success' : 'error',
        text: `${sent} enviadas, ${failed} falhas de ${jobsData.length} mensagens`,
      });

      fetchCampaign();
      fetchJobs();

      // Auto-continue if more jobs remain
      if (remainingCount > 0) {
        setTimeout(() => processQueueFromBrowser(), 1000);
        return; // Don't set processingBrowser false yet
      }
    } catch (err) {
      setQueueResult({
        type: 'error',
        text: `Erro: ${err instanceof Error ? err.message : 'Erro inesperado'}`,
      });
    }

    setProcessingBrowser(false);
    setBrowserProgress({ current: 0, total: 0 });
    setTimeout(() => setQueueResult(null), 8000);
  };

  // ===== NUMBER VERIFICATION =====
  const handleVerifyNumbers = async () => {
    if (!id || verifying) return;
    setVerifying(true);
    setVerifyResult(null);

    try {
      // 1. Get WhatsApp config
      const { data: waConfig } = await supabase
        .from('whatsapp_configs')
        .select('*')
        .maybeSingle();

      if (!waConfig?.evolution_server_url || !waConfig?.evolution_api_key || !waConfig?.evolution_instance_name) {
        setVerifyResult({ existing: 0, notExisting: 0, details: [] });
        setQueueResult({ type: 'error', text: 'Evolution API não configurada.' });
        setVerifying(false);
        return;
      }

      const baseUrl = waConfig.evolution_server_url.replace(/\/+$/, '');
      const instanceName = waConfig.evolution_instance_name;
      const apiKey = waConfig.evolution_api_key;

      // 2. Get all contacts from queued jobs for this campaign
      const { data: jobsData, error: jobsError } = await supabase
        .from('queue_jobs')
        .select('contact_id, contacts!inner(name, phone)')
        .eq('campaign_id', id)
        .eq('status', 'queued');

      if (jobsError || !jobsData || jobsData.length === 0) {
        setVerifyResult({ existing: 0, notExisting: 0, details: [] });
        setQueueResult({ type: 'error', text: 'Nenhum contato na fila para verificar.' });
        setVerifying(false);
        return;
      }

      // 3. Normalize all phone numbers
      const normalizePhone = (p: string): string => {
        const digits = p.replace(/\D/g, '');
        if (digits.startsWith('55')) return digits;
        return '55' + digits;
      };

      const contacts = (jobsData as unknown as Array<{
        contact_id: string;
        contacts: { name: string; phone: string };
      }>).map((row) => ({
        contactId: row.contact_id,
        contactName: row.contacts?.name || 'Sem nome',
        phone: normalizePhone(row.contacts?.phone || ''),
        phoneRaw: row.contacts?.phone || '',
      })).filter((c) => c.phone.length >= 12);

      if (contacts.length === 0) {
        setVerifyResult({ existing: 0, notExisting: 0, details: [] });
        setQueueResult({ type: 'error', text: 'Nenhum número válido para verificar.' });
        setVerifying(false);
        return;
      }

      // 4. Check numbers in batches via Evolution API
      // Try batch endpoint first: POST /chat/whatsappNumbers/{instance}
      const batchSize = 50;
      let allResults: Array<{ phoneRaw: string; contactName: string; exists: boolean }> = [];
      let existingCount = 0;
      let notExistingCount = 0;

      for (let batch = 0; batch * batchSize < contacts.length; batch++) {
        const batchContacts = contacts.slice(batch * batchSize, (batch + 1) * batchSize);
        const numbers = batchContacts.map((c) => c.phone);

        try {
          // Try batch endpoint
          const checkUrl = `${baseUrl}/chat/whatsappNumbers/${instanceName}`;
          const checkRes = await fetch(checkUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', apikey: apiKey },
            body: JSON.stringify({ numbers }),
          });

          if (checkRes.ok) {
            const checkData = await checkRes.json();
            // Response is array of { jid, exists, number }
            const results = Array.isArray(checkData)
              ? checkData
              : (checkData?.data || checkData?.response || []);

            const resultMap = new Map<string, boolean>();
            if (Array.isArray(results)) {
              results.forEach((r: Record<string, unknown>) => {
                if (r.number) resultMap.set(String(r.number), Boolean(r.exists));
              });
            }

            batchContacts.forEach((c) => {
              const exists = resultMap.get(c.phone) ?? false;
              allResults.push({ phoneRaw: c.phoneRaw, contactName: c.contactName, exists });
              if (exists) existingCount++; else notExistingCount++;
            });
          } else {
            // Fall back to individual checks
            for (const c of batchContacts) {
              try {
                const singleUrl = `${baseUrl}/chat/checkNumber/${instanceName}`;
                const singleRes = await fetch(singleUrl, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', apikey: apiKey },
                  body: JSON.stringify({ number: c.phone }),
                });
                const singleData = await singleRes.json();
                const exists = singleData?.exists === true || singleData?.numberExists === true;
                allResults.push({ phoneRaw: c.phoneRaw, contactName: c.contactName, exists });
                if (exists) existingCount++; else notExistingCount++;
              } catch {
                allResults.push({ phoneRaw: c.phoneRaw, contactName: c.contactName, exists: false });
                notExistingCount++;
              }
              await new Promise((r) => setTimeout(r, 200));
            }
          }
        } catch {
          // All checks failed for batch
          batchContacts.forEach((c) => {
            allResults.push({ phoneRaw: c.phoneRaw, contactName: c.contactName, exists: false });
            notExistingCount++;
          });
        }
      }

      setVerifyResult({
        existing: existingCount,
        notExisting: notExistingCount,
        details: allResults,
      });
    } catch (err) {
      setQueueResult({
        type: 'error',
        text: `Erro na verificação: ${err instanceof Error ? err.message : 'Erro inesperado'}`,
      });
    }

    setVerifying(false);
  };

  // Remove invalid numbers from queue (mark as failed)
  const handleRemoveInvalid = async () => {
    if (!verifyResult || removingInvalid) return;
    setRemovingInvalid(true);

    try {
      const notExistingPhones = verifyResult.details
        .filter((d) => !d.exists)
        .map((d) => d.phoneRaw);

      if (notExistingPhones.length === 0) {
        setQueueResult({ type: 'success', text: 'Nenhum número inválido para remover.' });
        setRemovingInvalid(false);
        return;
      }

      // Get all queued jobs for this campaign and mark the invalid ones
      const { data: allJobs } = await supabase
        .from('queue_jobs')
        .select('id, contact_id, contacts!inner(phone)')
        .eq('campaign_id', id)
        .eq('status', 'queued');

      if (!allJobs) {
        setRemovingInvalid(false);
        return;
      }

      const normalizePhone = (p: string): string => {
        const digits = p.replace(/\D/g, '');
        if (digits.startsWith('55')) return digits;
        return '55' + digits;
      };

      const invalidJobIds: string[] = [];
      (allJobs as unknown as Array<{ id: string; contact_id: string; contacts: { phone: string } }>).forEach((job) => {
        const normalized = normalizePhone(job.contacts?.phone || '');
        const isInvalid = notExistingPhones.some((raw) => normalizePhone(raw) === normalized);
        if (isInvalid) invalidJobIds.push(job.id);
      });

      if (invalidJobIds.length > 0) {
        // Mark as failed in batches
        for (let i = 0; i < invalidJobIds.length; i += 100) {
          const batch = invalidJobIds.slice(i, i + 100);
          await supabase
            .from('queue_jobs')
            .update({
              status: 'failed',
              last_error: 'Numero nao existe no WhatsApp (verificacao previa)',
              attempt: 3,
              processed_at: new Date().toISOString(),
            })
            .in('id', batch);
        }

        // Update campaign failed count directly
        const { data: currentCampaign } = await supabase
          .from('campaigns')
          .select('failed_count, sent_count, total_contacts')
          .eq('id', id)
          .maybeSingle();

        if (currentCampaign) {
          await supabase
            .from('campaigns')
            .update({
              failed_count: (currentCampaign.failed_count || 0) + invalidJobIds.length,
              updated_at: new Date().toISOString(),
            })
            .eq('id', id);
        }
      }

      setQueueResult({
        type: 'success',
        text: `${invalidJobIds.length} números sem WhatsApp removidos da fila.`,
      });
      setVerifyResult(null);
      fetchJobs();
      fetchCampaign();
    } catch (err) {
      setQueueResult({
        type: 'error',
        text: `Erro ao remover inválidos: ${err instanceof Error ? err.message : 'Erro inesperado'}`,
      });
    }

    setRemovingInvalid(false);
    setTimeout(() => setQueueResult(null), 6000);
  };

  // -- Loading state --
  if (loadingCampaign) {
    return (
      <div className="space-y-5">
        <Link to="/campaigns" className="text-sm text-foreground-500 hover:text-foreground-700 flex items-center gap-1">
          <i className="ri-arrow-left-line" /> Campanhas
        </Link>
        <div className="bg-white rounded-xl border border-background-200 p-12 flex items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-foreground-500">Carregando campanha...</span>
          </div>
        </div>
      </div>
    );
  }

  // -- Error state --
  if (fetchError || !campaign) {
    return (
      <div className="space-y-5">
        <Link to="/campaigns" className="text-sm text-foreground-500 hover:text-foreground-700 flex items-center gap-1">
          <i className="ri-arrow-left-line" /> Voltar para campanhas
        </Link>
        <div className="bg-white rounded-xl border border-background-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-red-500" />
          </div>
          <p className="text-sm text-foreground-700 font-medium mb-1">Campanha não encontrada</p>
          <p className="text-xs text-foreground-500 mb-4">{fetchError || 'A campanha pode ter sido excluída.'}</p>
          <Link
            to="/campaigns"
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
          >
            <i className="ri-arrow-left-line" /> Voltar para campanhas
          </Link>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(campaign.status);
  const deliveryRate = campaign.sent_count > 0 ? ((campaign.delivered_count / campaign.sent_count) * 100).toFixed(1) : '0';
  const readRate = campaign.delivered_count > 0 ? ((campaign.read_count / campaign.delivered_count) * 100).toFixed(1) : '0';

  // Stats from real jobs
  const jobsCompleted = jobs.filter((j) => j.status === 'completed').length;
  const jobsFailed = jobs.filter((j) => j.status === 'failed').length;
  const jobsProcessing = jobs.filter((j) => j.status === 'processing').length;
  const jobsQueued = jobs.filter((j) => j.status === 'queued').length;
  const totalJobs = jobs.length;

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <Link to="/campaigns" className="text-sm text-foreground-500 hover:text-foreground-700 flex items-center gap-1">
        <i className="ri-arrow-left-line" /> Campanhas
      </Link>

      {/* Header */}
      <div className="bg-white rounded-xl border border-background-200 p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-2 py-1 rounded-md text-xs font-medium border ${statusConfig.color}`}>
                {statusConfig.label}
              </span>
              {isRunning && (
                <span className="px-2 py-1 bg-accent-50 text-accent-700 rounded-md text-xs font-medium border border-accent-200 flex items-center gap-1">
                  <i className="ri-loader-4-line animate-spin" /> Processando
                </span>
              )}
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-950 mt-1">{campaign.name}</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {populateResult && (
              <div className={`text-xs px-3 py-1.5 rounded-lg border ${
                populateResult.type === 'success'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                {populateResult.text}
              </div>
            )}
            {queueResult && (
              <div className={`text-xs px-3 py-1.5 rounded-lg border ${
                queueResult.type === 'success'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                {queueResult.text}
              </div>
            )}
            <button
              onClick={handlePopulateQueue}
              disabled={populating}
              className="px-4 py-2 bg-secondary-500 hover:bg-secondary-600 disabled:bg-secondary-300 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {populating ? (
                <><i className="ri-loader-4-line animate-spin" /> Populando...</>
              ) : (
                <><i className="ri-play-list-add-line" /> Popular Fila</>
              )}
            </button>
            <button
              onClick={handleVerifyNumbers}
              disabled={verifying}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {verifying ? (
                <><i className="ri-loader-4-line animate-spin" /> Verificando...</>
              ) : (
                <><i className="ri-search-line" /> Verificar Números</>
              )}
            </button>
            <button
              onClick={handleProcessQueue}
              disabled={processingQueue || processingBrowser}
              className="px-4 py-2 bg-accent-500 hover:bg-accent-600 disabled:bg-accent-300 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {processingQueue ? (
                <><i className="ri-loader-4-line animate-spin" /> Processando...</>
              ) : (
                <><i className="ri-send-plane-fill" /> Processar Fila</>
              )}
            </button>
          </div>
        </div>

        {/* Browser processing progress */}
        {processingBrowser && (
          <div className="rounded-lg px-4 py-3 bg-sky-50 border border-sky-200 mb-4">
            <div className="flex items-center gap-3 mb-2">
              <i className="ri-loader-4-line animate-spin text-sky-600 text-lg" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-sky-700">Enviando mensagens pelo navegador...</p>
                <p className="text-xs text-sky-500">{browserProgress.current} de {browserProgress.total} mensagens</p>
              </div>
            </div>
            <div className="w-full h-2 bg-sky-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-sky-500 rounded-full transition-all duration-300"
                style={{ width: `${browserProgress.total > 0 ? Math.round((browserProgress.current / browserProgress.total) * 100) : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Verification results panel */}
        {verifyResult && (
          <div className="rounded-lg px-4 py-4 bg-amber-50 border border-amber-200 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <i className="ri-check-double-line text-amber-600 text-lg" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Resultado da Verificação</p>
                  <p className="text-xs text-amber-600">
                    {verifyResult.existing} com WhatsApp · {verifyResult.notExisting} sem WhatsApp
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setVerifyResult(null)}
                  className="text-xs text-amber-600 hover:text-amber-800 px-2 py-1 rounded-md hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Fechar
                </button>
                {verifyResult.notExisting > 0 && (
                  <button
                    onClick={handleRemoveInvalid}
                    disabled={removingInvalid}
                    className="px-3 py-1.5 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                  >
                    {removingInvalid ? (
                      <><i className="ri-loader-4-line animate-spin" /> Removendo...</>
                    ) : (
                      <><i className="ri-delete-bin-line" /> Remover {verifyResult.notExisting} inválidos</>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full h-2 bg-amber-100 rounded-full overflow-hidden mb-3">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${verifyResult.existing + verifyResult.notExisting > 0 ? Math.round((verifyResult.existing / (verifyResult.existing + verifyResult.notExisting)) * 100) : 0}%` }}
              />
            </div>

            {/* Scrollable list */}
            <div className="max-h-60 overflow-y-auto space-y-1">
              {verifyResult.details.slice(0, 100).map((d, idx) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs ${
                    d.exists ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <i className={`${d.exists ? 'ri-check-line text-emerald-500' : 'ri-close-line text-red-500'} flex-shrink-0`} />
                    <span className="truncate font-medium">{d.contactName}</span>
                  </div>
                  <span className="font-mono flex-shrink-0 ml-2">
                    {d.phoneRaw.startsWith('55') ? `+${d.phoneRaw}` : `+55${d.phoneRaw}`}
                  </span>
                </div>
              ))}
              {verifyResult.details.length > 100 && (
                <p className="text-xs text-foreground-400 text-center py-1">
                  + {verifyResult.details.length - 100} resultados não exibidos
                </p>
              )}
            </div>
          </div>
        )}

        {/* Big progress bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-foreground-700">Progresso do envio</span>
            <span className="text-2xl font-heading font-bold text-primary-600">{progress}%</span>
          </div>
          <div className="w-full h-4 bg-background-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-foreground-500">
            <span>{Math.round(campaign.total_contacts * (progress / 100)).toLocaleString('pt-BR')} enviadas</span>
            <span>{campaign.total_contacts.toLocaleString('pt-BR')} total</span>
          </div>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-background-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-heading font-bold text-foreground-950">{campaign.sent_count.toLocaleString('pt-BR')}</p>
            <p className="text-xs text-foreground-500 mt-0.5">Enviadas</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-heading font-bold text-emerald-700">{campaign.delivered_count.toLocaleString('pt-BR')}</p>
            <p className="text-xs text-emerald-600 mt-0.5">{deliveryRate}% entregues</p>
          </div>
          <div className="bg-primary-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-heading font-bold text-primary-700">{campaign.read_count.toLocaleString('pt-BR')}</p>
            <p className="text-xs text-primary-600 mt-0.5">{readRate}% lidas</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-heading font-bold text-red-700">{campaign.failed_count.toLocaleString('pt-BR')}</p>
            <p className="text-xs text-red-600 mt-0.5">Falhas</p>
          </div>
        </div>
      </div>

      {/* Queue visualization */}
      <div className="bg-white rounded-xl border border-background-200 p-5">
        <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">Fila de Processamento</h3>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-3 bg-background-100 rounded-full overflow-hidden flex">
              {campaign.sent_count > 0 && (
                <div
                  className="h-full bg-primary-500"
                  style={{ width: `${(campaign.sent_count / Math.max(campaign.total_contacts, 1)) * 100}%` }}
                />
              )}
              {campaign.failed_count > 0 && (
                <div
                  className="h-full bg-red-400"
                  style={{ width: `${(campaign.failed_count / Math.max(campaign.total_contacts, 1)) * 100}%` }}
                />
              )}
            </div>
          </div>
          <div className="flex items-center gap-5 text-xs text-foreground-500">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-primary-500" /> Enviadas
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-red-400" /> Falhas
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-background-200" /> Pendentes
            </div>
          </div>
        </div>
      </div>

      {/* Real-time Job Log Panel */}
      <div className="bg-white rounded-xl border border-background-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-heading font-semibold text-foreground-950">Jobs da Fila</h3>
            <p className="text-xs text-foreground-500 mt-0.5">Status de cada envio em tempo real</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${
                autoRefresh
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-background-100 border-background-200 text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? 'bg-emerald-500 animate-pulse' : 'bg-foreground-300'}`} />
              {autoRefresh ? 'Auto' : 'Pausado'}
            </button>
            <button
              onClick={() => fetchJobs()}
              className="flex items-center gap-1 text-xs font-medium text-foreground-500 hover:text-foreground-700 px-2 py-1.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line" /> Atualizar
            </button>
          </div>
        </div>

        {/* Stats mini-bar */}
        {totalJobs > 0 && (
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-background-100 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-foreground-600">{jobsCompleted} enviados</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-foreground-600">{jobsFailed} falhas</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse" />
              <span className="text-foreground-600">{jobsProcessing} enviando</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full bg-background-300" />
              <span className="text-foreground-600">{jobsQueued} na fila</span>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          {loadingJobs ? (
            <div className="flex items-center justify-center py-10">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-foreground-500">Carregando jobs da fila...</p>
              </div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-inbox-line text-xl text-foreground-400" />
              </div>
              <p className="text-sm text-foreground-500">Nenhum job na fila ainda</p>
              <p className="text-xs text-foreground-400 mt-1">
                Clique em <strong>"Popular Fila"</strong> para criar os jobs de envio com os contatos desta campanha.
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100">
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">#</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Contato</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Telefone</th>
                  <th className="text-center py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Status</th>
                  <th className="text-center py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Tent.</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Criado em</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Processado</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job, idx) => {
                  const badge = statusBadge[job.status];
                  return (
                    <tr key={job.id} className="border-b border-background-50 hover:bg-background-50/50 transition-colors group">
                      <td className="py-2.5 px-3 text-foreground-400 text-xs">{idx + 1}</td>
                      <td className="py-2.5 px-3 text-foreground-900 font-medium">{job.contactName}</td>
                      <td className="py-2.5 px-3 text-foreground-600 font-mono text-xs">{job.contactPhone.startsWith('55') ? job.contactPhone : '55' + job.contactPhone}</td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${badge.bg} ${badge.text}`}>
                          <i className={`${badge.icon} text-[10px]`} />
                          {statusLabel[job.status]}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={`text-xs font-medium ${
                          job.status === 'failed' && job.attempt >= job.maxAttempts ? 'text-red-600' : 'text-foreground-500'
                        }`}>
                          {job.attempt}/{job.maxAttempts}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs whitespace-nowrap">
                        {new Date(job.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </td>
                      <td className="py-2.5 px-3 text-foreground-500 text-xs whitespace-nowrap">
                        {job.processedAt
                          ? new Date(job.processedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {jobs.filter((j) => j.lastError).length > 0 && (
          <div className="mt-5 pt-4 border-t border-background-100">
            <h4 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-1.5">
              <i className="ri-error-warning-line" /> Erros de envio
            </h4>
            <div className="space-y-2">
              {jobs
                .filter((j) => j.lastError)
                .map((j) => (
                  <div key={`err-${j.id}`} className="bg-red-50/60 border border-red-100 rounded-lg px-3 py-2.5 text-sm">
                    <div className="flex items-start gap-2">
                      <i className="ri-close-circle-line text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-red-800 font-medium text-xs">
                          {j.contactName} ({j.contactPhone.startsWith('55') ? j.contactPhone : '55' + j.contactPhone})
                        </p>
                        <p className="text-red-600 text-xs mt-0.5 font-mono break-all">{j.lastError}</p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Campaign details */}
      <div className="bg-white rounded-xl border border-background-200 p-5">
        <h3 className="text-base font-heading font-semibold text-foreground-950 mb-3">Detalhes da Campanha</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-foreground-500 text-xs mb-0.5">Mensagem</p>
            <p className="text-foreground-900">{campaign.message_text}</p>
          </div>
          <div>
            <p className="text-foreground-500 text-xs mb-0.5">Público-alvo</p>
            <p className="text-foreground-900">
              {campaign.target_type === 'todos' ? 'Todos os contatos' : (campaign.target_groups || []).join(', ')}
            </p>
          </div>
          <div>
            <p className="text-foreground-500 text-xs mb-0.5">Criada em</p>
            <p className="text-foreground-900">
              {new Date(campaign.created_at).toLocaleDateString('pt-BR', {
                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </p>
          </div>
          <div>
            <p className="text-foreground-500 text-xs mb-0.5">Agendamento</p>
            <p className="text-foreground-900">
              {campaign.scheduled_at
                ? new Date(campaign.scheduled_at).toLocaleDateString('pt-BR', {
                    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                  })
                : 'Não agendada'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}